'use client';

import React, { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface PuppyCardProps {
  puppy: {
    id: string;
    name: string;
    variety: string;
    age: string;
    gender: string;
    price: number;
    availability: 'available' | 'reserved' | 'sold';
    images: Array<{ url: string; alt: string }>;
    description: string;
    weight: string;
    parentInfo: string;
    healthCertifications: string[];
  };
  onInquire: (puppyId: string, puppyName: string) => void;
}

const PuppyCard = ({ puppy, onInquire }: PuppyCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHydrated, setIsHydrated] = useState(false);

  React.useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => 
      prev === 0 ? puppy.images.length - 1 : prev - 1
    );
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => 
      prev === puppy.images.length - 1 ? 0 : prev + 1
    );
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleInquireClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onInquire(puppy.id, puppy.name);
  };

  const getAvailabilityColor = () => {
    switch (puppy.availability) {
      case 'available':
        return 'bg-success/10 text-success';
      case 'reserved':
        return 'bg-warning/10 text-warning';
      case 'sold':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getAvailabilityText = () => {
    switch (puppy.availability) {
      case 'available':
        return 'Available';
      case 'reserved':
        return 'Reserved';
      case 'sold':
        return 'Sold';
      default:
        return 'Unknown';
    }
  };

  if (!isHydrated) {
    return (
      <div className="overflow-hidden rounded-xl bg-card shadow-warm transition-smooth">
        <div className="relative aspect-[4/3] bg-muted" />
        <div className="p-5">
          <div className="mb-3 h-7 w-3/4 rounded bg-muted" />
          <div className="mb-4 space-y-2">
            <div className="h-4 w-full rounded bg-muted" />
            <div className="h-4 w-2/3 rounded bg-muted" />
          </div>
          <div className="h-12 w-full rounded-md bg-muted" />
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`overflow-hidden rounded-xl bg-card shadow-warm transition-smooth hover:shadow-warm-md ${
        isExpanded ? 'ring-3 ring-primary/20' : ''
      }`}
    >
      {/* Image Gallery */}
      <div className="relative aspect-[3/4] overflow-hidden bg-muted">
        <AppImage
          src={puppy.images[currentImageIndex].url}
          alt={puppy.images[currentImageIndex].alt}
          width={400}
          height={533}
          quality={85}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px"
          className="h-full w-full object-cover"
          onClick={toggleExpanded}
        />

        {/* Availability Badge */}
        <div className="absolute left-4 top-4">
          <span className={`rounded-full px-3 py-1.5 text-xs font-semibold ${getAvailabilityColor()}`}>
            {getAvailabilityText()}
          </span>
        </div>

        {/* Image Navigation */}
        {puppy.images.length > 1 && (
          <>
            <button
              onClick={handlePrevImage}
              className="absolute left-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-card/90 text-foreground shadow-warm transition-smooth hover:bg-card hover:scale-110 focus-ring"
              aria-label="Previous image"
            >
              <Icon name="ChevronLeftIcon" size={20} variant="solid" />
            </button>
            <button
              onClick={handleNextImage}
              className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-card/90 text-foreground shadow-warm transition-smooth hover:bg-card hover:scale-110 focus-ring"
              aria-label="Next image"
            >
              <Icon name="ChevronRightIcon" size={20} variant="solid" />
            </button>

            {/* Image Indicators */}
            <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2">
              {puppy.images.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(index);
                  }}
                  className={`h-2 rounded-full transition-smooth ${
                    index === currentImageIndex
                      ? 'w-6 bg-primary' :'w-2 bg-card/60 hover:bg-card/80'
                  }`}
                  aria-label={`View image ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Puppy Information */}
      <div className="p-5">
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h3 className="mb-3 flex items-start gap-2 font-heading text-lg font-semibold text-foreground">
              <Icon name="ChatBubbleLeftEllipsisIcon" size={20} className="mt-1 shrink-0 text-primary" />
              {puppy.name}
            </h3>
            <p className="text-sm text-muted-foreground">{puppy.variety}</p>
          </div>
          <div className="text-right">
            <p className="font-heading text-2xl font-bold text-primary">
              From $500 - $700
            </p>
          </div>
        </div>

        {/* Quick Info */}
        <div className="mb-4 grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2 text-sm text-foreground">
            <Icon name="CalendarIcon" size={16} variant="outline" className="text-primary" />
            <span>{puppy.age}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-foreground">
            <Icon name="HeartIcon" size={16} variant="outline" className="text-primary" />
            <span>{puppy.gender}</span>
          </div>
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="mb-4 space-y-4 border-t border-border pt-4">
            <div>
              <h4 className="mb-2 text-sm font-semibold text-foreground">About This Puppy</h4>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {puppy.description}
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <h4 className="mb-1 text-sm font-semibold text-foreground">Weight</h4>
                <p className="text-sm text-muted-foreground">{puppy.weight}</p>
              </div>
              <div>
                <h4 className="mb-1 text-sm font-semibold text-foreground">Parents</h4>
                <p className="text-sm text-muted-foreground">{puppy.parentInfo}</p>
              </div>
            </div>

            <div>
              <h4 className="mb-2 text-sm font-semibold text-foreground">Health Checks</h4>
              <ul className="space-y-1">
                {puppy.healthCertifications.map((cert, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Icon name="CheckCircleIcon" size={16} variant="solid" className="mt-0.5 text-success" />
                    <span>{cert}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={toggleExpanded}
            className="flex flex-1 items-center justify-center gap-2 rounded-md border border-border bg-card px-4 py-3 text-sm font-medium text-foreground transition-smooth hover:bg-muted focus-ring"
          >
            {isExpanded ? 'Show Less' : 'View Details'}
            <Icon 
              name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} 
              size={16} 
            />
          </button>
          <button
            onClick={handleInquireClick}
            disabled={puppy.availability === 'sold'}
            className="flex flex-1 items-center justify-center gap-2 rounded-md bg-accent px-4 py-3 text-sm font-medium text-accent-foreground shadow-warm transition-smooth hover:scale-[0.97] hover:shadow-warm-md focus-ring disabled:cursor-not-allowed disabled:opacity-50"
          >
            Ask About This Puppy
            <Icon name="PaperAirplaneIcon" size={16} variant="solid" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PuppyCard;